import React, { memo, useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { BUTTON_ROLES, CFD, ETF, FX } from 'shared-modules/constants';
import {
  MAINTENANCE_INFOPAGE_URL,
  FORGOT_EMERGENCY_ID_URL,
  PASSKEY_DETAIL_URL,
  PASSKEY_LOGIN_ISSUE_URL,
  INQUIRY_LINK,
  MY_PAGE_HOME_URL,
  FA_JS_LOGIN,
} from 'shared-modules/config';
import { checkIsWebApp } from 'shared-modules/services';

import {
  MAINTENANCE_LOGIN_ERROR_MESSAGE,
  MAINTENANCE_ERROR_MESSAGE_MOBILE,
  DISPLAYING_ERROR_IMS,
  DISPLAYING_PASSKEY_ERROR_IMS,
  IMS_200B_ERROR_MESSAGE,
  UNEXPECTED_ERROR_IN_PASSKEY_MESSAGE,
} from 'shared-modules/constants/errorMesages';
import { IMS_LOGIN_ACCOUNT_TYPE } from 'shared-modules/constants/apiConstant';
import { fetchNotification } from 'shared-modules/api/generalApi';
import { getSettings } from 'shared-modules/api/settingsApi';
import { useFraudAlertScript } from '../../hooks';
import { useKeyPressHandler } from '../../services/hooks';
import {
  loginUserFailed,
  loginUserRequest,
  passkeyChallengeRequest,
  passkeyVerifyRequest,
  resetLoginError,
} from '../../redux/actions';
import CustomInput from '../../components/CustomInput';
import CustomButton from '../../components/CustomButton';
import LoginAuthPage from '../LoginAuthPage';
import styles from './loginPage.module.scss';
import triautoLogo from '../../assets/login/logo.svg';
import passkeyIllustration from '../../assets/login/passkey.svg';
import { NotificationCard } from './NotificationCard';
import { MaintenanceErrorText } from './MaintenanceErrorText';
import mockPasskey from '../../services/mockPasskey';

const LOGIN_PAGES = {
  LOGIN: 'login',
  AUTH: 'auth',
};

const LOGIN_SOURCE = {
  PASSWORD: 'password',
  PASSKEY: 'passkey',
};

const isPasskeyCancelledError = (error) => error instanceof DOMException && error.name === 'NotAllowedError';

const base64urlToArrayBuffer = (base64url) => {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
  const binary = atob(padded);
  return Uint8Array.from(binary, (c) => c.charCodeAt(0)).buffer;
};

const normalizePublicKeyRequestOptions = (publicKey) => {
  const normalized = {
    ...publicKey,
    challenge: base64urlToArrayBuffer(publicKey.challenge),
    timeout: publicKey.timeout,
    allowCredentials: (publicKey.allowCredentials || [])
      .filter((credential) => credential?.id)
      .map((credential) => ({
        ...credential,
        id: base64urlToArrayBuffer(credential.id),
      })),
  };

  return normalized;
};

const LoginPage = () => {
  const dispatch = useDispatch();
  const [activePage, setActivePage] = useState(LOGIN_PAGES.LOGIN);

  const [bannerTitle, setBannerTitle] = useState([]);
  const [maintenanceInfo, setMaintenanceInfo] = useState([]);

  const fetchBanner = useCallback(async () => {
    const { data } = await fetchNotification();
    setBannerTitle(data);
    return setBannerTitle;
  }, [setBannerTitle]);

  const getMaintenanceInfo = useCallback(async () => {
    const {
      data: {
        [ETF]: { system_status: etfSystemStatus },
        [FX]: { system_status: fxSystemStatus },
        [CFD]: { system_status: cfdSystemStatus },
      },
    } = await getSettings({ isPublic: true });
    setMaintenanceInfo({ etfSystemStatus, fxSystemStatus, cfdSystemStatus });
    return setMaintenanceInfo;
  }, [setMaintenanceInfo]);

  const isFxMaintenance = maintenanceInfo.fxSystemStatus === '2';
  const isEtfMaintenance = maintenanceInfo.etfSystemStatus === '2';
  const isCfdMaintenance = maintenanceInfo.cfdSystemStatus === '2';
  const isMaintenance = isEtfMaintenance && isFxMaintenance && isCfdMaintenance;

  const listContainer = useMemo(() => {
    return bannerTitle.map((item) => (
      <NotificationCard title={item.title} src={item.body} link={item.url} key={item.notificationId} />
    ));
  }, [bannerTitle]);

  const accountType = useSelector((state) => state.settings.loginMethod);
  const loginFlowIsLoading = useSelector((state) => state.auth.loginFlowIsLoading);
  useEffect(() => {
    fetchBanner();
    getMaintenanceInfo();
  }, [dispatch, fetchBanner, getMaintenanceInfo]);

  const [login, changeLogin] = useState('');
  const [password, changePassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginRequested, setLoginRequested] = useState(null);
  const [loginSource, setLoginSource] = useState(LOGIN_SOURCE.PASSWORD);

  const error = useSelector((state) => state.auth.error);
  const errorMessages = useSelector((state) => state.auth.errorMessages);
  const loginErrorMessage = useSelector((state) => state.auth.loginErrorMessage);
  const isDefaultErrorMessage = IMS_200B_ERROR_MESSAGE === loginErrorMessage;

  const authIsLoading = useSelector((state) => state.auth.isLoading);
  const isEmergency = useMemo(() => accountType !== IMS_LOGIN_ACCOUNT_TYPE, [accountType]);
  const otpRequired = useSelector((state) => state.auth.otpRequired);
  const [passkeyProcessing, setPasskeyProcessing] = useState(false);

  const handleResetLoginError = useCallback(() => dispatch(resetLoginError({ name: 'login' })), [dispatch]);
  const handleResetPasswordError = useCallback(() => dispatch(resetLoginError({ name: 'password' })), [dispatch]);
  const openMyPageTab = useCallback(() => window.open(MY_PAGE_HOME_URL, '_blank'), []);
  const openForgotIdTab = useCallback(() => window.open(FORGOT_EMERGENCY_ID_URL, '_blank'), []);
  const openForgotIdTabByKey = useKeyPressHandler(openForgotIdTab);
  const openInquiryTab = useCallback(() => window.open(INQUIRY_LINK, '_blank'), []);
  const openInquiryTabByKey = useKeyPressHandler(openInquiryTab);
  const loginQuestionTab = useCallback(() => window.open(PASSKEY_LOGIN_ISSUE_URL, '_blank'), []);
  const loginQuestionTabByKey = useKeyPressHandler(loginQuestionTab);
  const passkeyDetailTab = useCallback(() => window.open(PASSKEY_DETAIL_URL, '_blank'), []);
  const passkeyDetailTabByKey = useKeyPressHandler(passkeyDetailTab);
  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      if (!login || !password || error || authIsLoading || isMaintenance) {
        return;
      }
      setLoginSource(LOGIN_SOURCE.PASSWORD);
      setLoginRequested((current) => (current ?? 0) + 1);
      dispatch(
        loginUserRequest({
          login,
          password,
          accountType,
        }),
      );
    },
    [login, password, error, authIsLoading, isMaintenance, dispatch, accountType],
  );
  const getPasskeyErrorMessage = useCallback(
    (e) => {
      if (e.name === 'NotSupportedError') {
        return (
          <>
            お使いの端末またはブラウザはパスキー認証に対応していません。パスキーログインでお困りの方は
            <div className={styles.emergencyTextLink} onClick={loginQuestionTab} role="button" aria-hidden>
              こちら
            </div>
            をご確認ください。
          </>
        );
      }
      return (
        <>
          パスキーログインを完了できませんでした。再度お試しいただくか、ID・パスワードでログインしてください。
          パスキーログインでお困りの方は
          <div className={styles.emergencyTextLink} onClick={loginQuestionTab} role="button" aria-hidden>
            こちら
          </div>
          をご確認ください。
        </>
      );
    },
    [loginQuestionTab],
  );

  const handlePasskeyLogin = useCallback(() => {
    try {
      setLoginSource(LOGIN_SOURCE.PASSKEY);
      if (isEmergency) {
        dispatch(loginUserFailed({ errorMessages: [], loginErrorMessage: DISPLAYING_PASSKEY_ERROR_IMS }));
        return;
      }
      setPasskeyProcessing(true);

      dispatch(
        passkeyChallengeRequest({
          successCallback: async ({ token, publicKey }) => {
            try {
              const credential = await navigator.credentials.get({
                publicKey: {
                  ...normalizePublicKeyRequestOptions(publicKey),
                },
              });

              if (!credential) {
                setPasskeyProcessing(false);
                return;
              }

              dispatch(
                passkeyVerifyRequest({
                  credential,
                  token,
                  successCallback: ({ username }) => {
                    dispatch(
                      loginUserRequest({
                        login: username,
                        password: token,
                        accountType: 'passkey',
                        successCallback: () => {
                          setPasskeyProcessing(false);
                        },
                        errorCallback: () => {
                          setPasskeyProcessing(false);
                        },
                      }),
                    );
                  },
                  errorCallback: (_, message) => {
                    dispatch(loginUserFailed({ errorMessages: [], loginErrorMessage: message || '' }));
                    setPasskeyProcessing(false);
                  },
                }),
              );
            } catch (e) {
              if (!isPasskeyCancelledError(e)) {
                dispatch(loginUserFailed({ errorMessages: [], loginErrorMessage: getPasskeyErrorMessage(e) }));
              }
              setPasskeyProcessing(false);
            }
          },
          errorCallback: (_, message) => {
            dispatch(loginUserFailed({ errorMessages: [], loginErrorMessage: message || '' }));
            setPasskeyProcessing(false);
          },
        }),
      );
    } catch (e) {
      dispatch(loginUserFailed({ errorMessages: [], loginErrorMessage: UNEXPECTED_ERROR_IN_PASSKEY_MESSAGE }));
      setPasskeyProcessing(false);
    }
  }, [dispatch, isEmergency, getPasskeyErrorMessage]);

  const maintenanceMessage = () => {
    const message = checkIsWebApp() ? MAINTENANCE_LOGIN_ERROR_MESSAGE : MAINTENANCE_ERROR_MESSAGE_MOBILE;
    return <MaintenanceErrorText message={message} linkURL={MAINTENANCE_INFOPAGE_URL} />;
  };

  const handleCancelAuth = useCallback(() => {
    dispatch(loginUserFailed({ errorMessages: [], loginErrorMessage: '', keepOtpRequired: false }));
    setActivePage(LOGIN_PAGES.LOGIN);
  }, [dispatch]);

  useEffect(() => {
    if (!otpRequired && (error || loginErrorMessage)) {
      setActivePage(LOGIN_PAGES.LOGIN);
    }
  }, [error, loginErrorMessage, otpRequired]);

  useEffect(() => {
    setActivePage(otpRequired ? LOGIN_PAGES.AUTH : LOGIN_PAGES.LOGIN);
  }, [otpRequired]);

  const otpTrans = useRef(otpRequired);
  useEffect(() => {
    otpTrans.current = otpRequired;
  }, [otpRequired]);

  const fraudAlertReloadTrigger = loginErrorMessage || otpTrans.current ? loginRequested : null;

  useFraudAlertScript(FA_JS_LOGIN, fraudAlertReloadTrigger);

  useEffect(() => {
    if (fraudAlertReloadTrigger !== null && otpTrans.current) {
      otpTrans.current = false;
    }
  }, [fraudAlertReloadTrigger]);

  // -- mock create key start --
  const [visibleMock, setVisibleMock] = useState(mockPasskey.isMockPasskey);
  // -- mock create key end --

  if (activePage === LOGIN_PAGES.AUTH) {
    return (
      <LoginAuthPage
        login={login}
        onCancel={handleCancelAuth}
        onSubmit={() => setLoginRequested((current) => (current ?? 0) + 1)}
      />
    );
  }

  // ID・パスワードでログインでエラーがあるとき、gapの値が変わることの対応
  const hasLoginErrorMessage = loginErrorMessage && loginSource === LOGIN_SOURCE.PASSWORD;
  const hasIdPswError = hasLoginErrorMessage || isDefaultErrorMessage;

  return (
    <div className={styles.wrapper}>
      {!accountType || loginFlowIsLoading ? (
        <div className={classNames('spinner-border', styles.loader)} role="status">
          <span className="sr-only">Loading...</span>
        </div>
      ) : (
        <div className={styles.container}>
          <div className={styles.containerLeft}>
            <div className={styles.leftContent}>
              <div className={styles.logoWrapper} style={isEmergency ? { paddingTop: 0 } : undefined}>
                <img src={triautoLogo} className={styles.logo} alt="トライオート" />
              </div>
              {!isEmergency && (
                <div
                  className={styles.passkeyCard}
                  onClick={passkeyDetailTab}
                  onKeyPress={passkeyDetailTabByKey}
                  role="button"
                  tabIndex={0}
                >
                  <img
                    src={passkeyIllustration}
                    className={styles.passkeyIllustration}
                    alt="パスキー認証をおすすめします"
                  />
                </div>
              )}
            </div>
            {listContainer.length > 0 && <div className={styles.containerLeftSmall}>{listContainer}</div>}
          </div>
          <div className={styles.containerRight}>
            <div className={styles.rightContent}>
              <div className={styles.loginTitle}>
                <div className={styles.title}>ログイン</div>
                <div className={styles.subTitle}>トライオートの取引画面にログインします。</div>
              </div>
              <div className={styles.rightController}>
                <div className={styles.formSection}>
                  <form onSubmit={handleSubmit} autoComplete="off">
                    <div className={classNames(styles.loginForm, { [styles.loginFormHasError]: hasIdPswError })}>
                      <div className={styles.inputGroup}>
                        {isEmergency && (
                          <div className={styles.imsEmergencyText}>
                            <span /> {DISPLAYING_ERROR_IMS}
                          </div>
                        )}
                        <div className={styles.loginInput}>
                          <div>
                            <CustomInput
                              label=""
                              placeholder={isEmergency ? '取引ログインID' : 'メールアドレスまたはMyページログインID'}
                              value={login}
                              onChange={changeLogin}
                              // カウリス側で画面要素を取得するため、nameを設定
                              name="login"
                              errorMessages={errorMessages}
                              validateFunction={handleResetLoginError}
                              inputClassName={styles.input}
                              errorClassName={styles.idError}
                            />
                          </div>
                          <div className={styles.personIconWrapper}>
                            <i className={classNames('material-icons', styles.personIcon)}>person</i>
                          </div>
                        </div>
                        <div className={styles.loginInput}>
                          <div>
                            <CustomInput
                              label=""
                              placeholder={isEmergency ? '取引パスワード' : 'Myページパスワード'}
                              value={password}
                              onChange={changePassword}
                              name="password"
                              type={showPassword ? 'text' : 'password'}
                              errorMessages={errorMessages}
                              validateFunction={handleResetPasswordError}
                              inputClassName={styles.input}
                              errorClassName={styles.pwError}
                            />
                          </div>
                          <div className={styles.lockIconWrapper}>
                            <i className={classNames('material-icons', styles.lockIcon)}>lock</i>
                          </div>
                          <div className={styles.eyeIconWrapper}>
                            <span
                              className={styles.eyeIcon}
                              onClick={() => setShowPassword(!showPassword)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  setShowPassword(!showPassword);
                                }
                              }}
                              role="button"
                              tabIndex={0}
                            >
                              {showPassword ? (
                                <i className="material-icons">visibility_off</i>
                              ) : (
                                <i className="material-icons">visibility</i>
                              )}
                            </span>
                          </div>
                        </div>
                      </div>
                      {hasIdPswError && (
                        <div>
                          {hasLoginErrorMessage && (
                            // カウリス側で画面要素を取得するため、idを付与
                            <div id="loginErrorMessage" className={styles.emergencyText}>
                              {loginErrorMessage}
                            </div>
                          )}
                          {isDefaultErrorMessage && (
                            <div className={styles.emergencyText}>
                              初期パスワードの場合は
                              <div
                                className={styles.emergencyTextLink}
                                onClick={openMyPageTab}
                                role="button"
                                aria-hidden
                              >
                                Myページ
                              </div>
                              にログインしてパスワードを変更してください。
                            </div>
                          )}
                        </div>
                      )}
                      {isMaintenance && <div className={styles.maintenanceText}>{maintenanceMessage()}</div>}
                      <CustomButton
                        isDisabled={!login || !password || error || passkeyProcessing || authIsLoading}
                        isLoading={authIsLoading}
                        buttonRole={BUTTON_ROLES.SUBMIT}
                        buttonClassName={styles.conbutton}
                      >
                        ID・パスワードでログイン
                      </CustomButton>
                    </div>
                  </form>
                  <div className={styles.forgotLinkWrapper}>
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={isEmergency ? openInquiryTab : openForgotIdTab}
                      onKeyPress={isEmergency ? openInquiryTabByKey : openForgotIdTabByKey}
                      className={styles.forgotLink}
                    >
                      {isEmergency ? 'お問い合わせはこちら' : 'MyページID・パスワードをお忘れの方'}
                    </span>
                  </div>
                </div>
                <div className={styles.divider}>
                  <div className={styles.dividerLine} />
                  <span className={styles.dividerText}>または</span>
                  <div className={styles.dividerLine} />
                </div>
                <CustomButton
                  buttonRole={BUTTON_ROLES.BUTTON}
                  onClick={handlePasskeyLogin}
                  isDisabled={passkeyProcessing || authIsLoading || isEmergency}
                  isLoading={passkeyProcessing}
                  buttonClassName={styles.passkeyButton}
                >
                  パスキーでログイン
                </CustomButton>
                {loginErrorMessage && loginSource === LOGIN_SOURCE.PASSKEY && (
                  <div id="loginErrorMessagePasskey" className={styles.emergencyText}>
                    {loginErrorMessage}
                  </div>
                )}
                <div className={styles.helpLinkWrapper}>
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={loginQuestionTab}
                    onKeyPress={loginQuestionTabByKey}
                    className={styles.helpLink}
                  >
                    パスキーログインでお困りの方
                  </span>
                </div>
              </div>
            </div>
            {visibleMock && (
              <div
                style={{
                  backgroundColor: 'yellow',
                  display: 'flex',
                  flexDirection: 'row',
                  gap: '20px',
                }}
              >
                <CustomButton
                  buttonClassName={styles.conbutton}
                  onClick={() => {
                    if (!login) {
                      // eslint-disable-next-line no-alert
                      window.alert('ログインIDを入力してください');
                      return;
                    }

                    (async () => {
                      try {
                        const credential = await mockPasskey.createPasskey({ login });
                        // eslint-disable-next-line no-console
                        console.log('create key ', JSON.stringify(credential));
                      } catch (e) {
                        // eslint-disable-next-line no-console
                        console.error(e);
                      }
                    })();
                  }}
                >
                  create key
                </CustomButton>
                <CustomButton
                  buttonClassName={styles.conbutton}
                  onClick={() => {
                    (async () => {
                      try {
                        const assertion = await mockPasskey.getPasskey();
                        // eslint-disable-next-line no-console
                        console.log('get key ', JSON.stringify(assertion));
                      } catch (e) {
                        // eslint-disable-next-line no-console
                        console.error(e);
                      }
                    })();
                  }}
                >
                  get key
                </CustomButton>
                <div style={{ marginTop: '70px' }}>
                  <span
                    role="button"
                    tabIndex={0}
                    style={{ color: 'black' }}
                    onClick={() => {
                      setVisibleMock(false);
                    }}
                    onKeyPress={undefined}
                  >
                    非表示
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(LoginPage);
